import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, Download, Loader2, TrendingDown, TrendingUp, Tag, Save, Trash2, Package, ListChecks,
} from "lucide-react";
import Navigation from "@/components/Navigation";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const kr = (v) => `${Math.round(Number(v) || 0).toLocaleString("sv-SE")} kr`;

function toCSV(rows, headers) {
  const esc = (v) => {
    const s = v === null || v === undefined ? "" : String(v);
    return /[";\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const head = headers.map(h => esc(h.label)).join(";");
  const body = rows.map(r => headers.map(h => esc(h.get(r))).join(";")).join("\n");
  return "\ufeff" + head + "\n" + body; // BOM for Excel (sv)
}

function download(filename, content) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

export default function BlackFridayPage() {
  const navigate = useNavigate();
  const [months, setMonths] = useState(6);
  const [debouncedMonths, setDebouncedMonths] = useState(6);
  useEffect(() => { const t = setTimeout(() => setDebouncedMonths(months), 400); return () => clearTimeout(t); }, [months]);
  const [tab, setTab] = useState("dead"); // dead | top | campaigns
  const [loading, setLoading] = useState(false);
  const [dead, setDead] = useState({ items: [], total_stock_value_sek: 0, count: 0 });
  const [top, setTop] = useState({ items: [], count: 0 });
  const [selected, setSelected] = useState({});
  const [campaigns, setCampaigns] = useState([]);
  const [showSave, setShowSave] = useState(false);
  const [campaignName, setCampaignName] = useState("");
  const [viewCampaign, setViewCampaign] = useState(null);

  const loadDead = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/dead-stock`, { params: { months: m } });
      setDead(res.data); setSelected({});
    } catch { toast.error("Kunde inte hämta dödlager"); }
    finally { setLoading(false); }
  }, []);

  const loadTop = useCallback(async (m) => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/inventory/top-sellers`, { params: { months: m, limit: 100 } });
      setTop(res.data);
    } catch { toast.error("Kunde inte hämta toppsäljare"); }
    finally { setLoading(false); }
  }, []);

  const loadCampaigns = useCallback(async () => {
    try { const res = await axios.get(`${API}/inventory/campaigns`); setCampaigns(res.data || []); } catch { /* */ }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => {
    if (tab === "dead") loadDead(debouncedMonths);
    else if (tab === "top") loadTop(debouncedMonths);
  }, [tab, debouncedMonths, loadDead, loadTop]);

  const selectedItems = dead.items.filter(i => selected[i.ean]);
  const allSelected = dead.items.length > 0 && selectedItems.length === dead.items.length;

  const exportDead = () => {
    const rows = selectedItems.length ? selectedItems : dead.items;
    const headers = [
      { label: "Artikelnummer", get: r => r.artikelnummer },
      { label: "EAN", get: r => r.ean },
      { label: "Produktnamn", get: r => r.produktnamn },
      { label: "Tillverkare", get: r => r.tillverkare },
      { label: "Antal i lager", get: r => r.antal },
      { label: "Lagerplatser", get: r => r.locations },
      { label: "Senaste rörelse", get: r => r.last_movement ? String(r.last_movement).slice(0, 10) : "Aldrig" },
      { label: "Dagar sedan rörelse", get: r => r.days_since_movement ?? "" },
      { label: "Inköpspris (kr)", get: r => r.unit_price_sek ?? "" },
      { label: "Lagervärde (kr)", get: r => r.stock_value_sek },
      { label: "Föreslagen rabatt (%)", get: r => r.suggested_discount },
    ];
    download(`dodlager_${months}man_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows, headers));
    toast.success(`Exporterade ${rows.length} produkter`);
  };

  const exportTop = () => {
    const headers = [
      { label: "Artikelnummer", get: r => r.artikelnummer },
      { label: "EAN", get: r => r.ean },
      { label: "Produktnamn", get: r => r.produktnamn },
      { label: "Sålt antal", get: r => r.qty },
      { label: "Nu i lager", get: r => r.in_stock },
    ];
    download(`toppsaljare_${months}man_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(top.items, headers));
    toast.success(`Exporterade ${top.items.length} produkter`);
  };

  const saveCampaign = async () => {
    const items = selectedItems.length ? selectedItems : dead.items;
    if (items.length === 0) { toast.error("Inga produkter att spara"); return; }
    try {
      await axios.post(`${API}/inventory/campaigns`, { name: campaignName, months, items });
      toast.success(`Kampanjlista "${campaignName}" sparad (${items.length} produkter)`);
      setShowSave(false); setCampaignName(""); loadCampaigns();
    } catch (e) { toast.error(e.response?.data?.detail || "Kunde inte spara"); }
  };

  const openCampaign = async (id) => {
    try { const res = await axios.get(`${API}/inventory/campaigns/${id}`); setViewCampaign(res.data); } catch { toast.error("Kunde inte öppna"); }
  };
  const deleteCampaign = async (id) => {
    if (!window.confirm("Ta bort kampanjlistan? Detta går inte att ångra.")) return;
    try { await axios.delete(`${API}/inventory/campaigns/${id}`); toast.success("Kampanjlista borttagen"); loadCampaigns(); } catch { toast.error("Kunde inte ta bort"); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-6 space-y-5" data-testid="black-friday-page">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/inventory")} data-testid="bf-back"><ArrowLeft className="h-5 w-5" /></Button>
          <Tag className="h-6 w-6 text-primary" />
          <div>
            <h1 className="font-heading text-2xl font-bold uppercase tracking-tight">Black Friday</h1>
            <p className="text-sm text-muted-foreground">Dödlager, toppsäljare och kampanjlistor</p>
          </div>
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="p-4 flex flex-wrap items-end gap-4">
            <div className="flex gap-1 rounded-lg border p-1">
              {[["dead", "Dödlager", TrendingDown], ["top", "Toppsäljare", TrendingUp], ["campaigns", "Kampanjlistor", ListChecks]].map(([id, label, Icon]) => (
                <Button key={id} variant={tab === id ? "default" : "ghost"} className="gap-2" onClick={() => setTab(id)} data-testid={`bf-tab-${id}`}>
                  <Icon className="h-4 w-4" />{label}
                </Button>
              ))}
            </div>
            {tab !== "campaigns" && (
              <div className="w-36">
                <Label className="text-xs">Intervall (månader)</Label>
                <Input type="number" min="1" max="60" value={months}
                  onChange={(e) => setMonths(Math.max(1, Number(e.target.value) || 1))}
                  data-testid="bf-months" />
              </div>
            )}
          </CardContent>
        </Card>

        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-muted-foreground" /></div>
        ) : tab === "dead" ? (
          <>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex gap-4 text-sm">
                <span><span className="font-bold text-foreground">{dead.count}</span> <span className="text-muted-foreground">produkter utan rörelse på {months} mån</span></span>
                <span><span className="font-bold text-emerald-500">{kr(dead.total_stock_value_sek)}</span> <span className="text-muted-foreground">bundet lagervärde</span></span>
              </div>
              <div className="flex gap-2">
                <Button variant="outline" className="gap-2" onClick={() => setShowSave(true)} disabled={dead.items.length === 0} data-testid="bf-save-campaign">
                  <Save className="h-4 w-4" />Spara som kampanjlista{selectedItems.length ? ` (${selectedItems.length})` : ""}
                </Button>
                <Button className="gap-2" onClick={exportDead} disabled={dead.items.length === 0} data-testid="bf-export-dead">
                  <Download className="h-4 w-4" />Exportera CSV{selectedItems.length ? ` (${selectedItems.length})` : ""}
                </Button>
              </div>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="grid grid-cols-[32px_150px_1fr_80px_90px_110px_110px_80px] gap-3 p-3 border-b bg-muted/50 text-xs font-medium items-center">
                  <Checkbox checked={allSelected} onCheckedChange={(v) => setSelected(v ? Object.fromEntries(dead.items.map(i => [i.ean, true])) : {})} data-testid="bf-select-all" />
                  <div>Artikel</div><div>Produkt</div><div>Antal</div><div>Dagar sedan</div><div>Lagervärde</div><div>Lagerplats</div><div>Rabatt</div>
                </div>
                <div className="divide-y max-h-[60vh] overflow-auto">
                  {dead.items.length === 0 ? (
                    <div className="p-10 text-center text-muted-foreground"><Package className="h-8 w-8 mx-auto mb-2 opacity-40" />Inga produkter utan rörelse i detta intervall</div>
                  ) : dead.items.map(i => (
                    <div key={i.ean} className="grid grid-cols-[32px_150px_1fr_80px_90px_110px_110px_80px] gap-3 p-3 items-center text-sm hover:bg-muted/30" data-testid={`bf-dead-row-${i.ean}`}>
                      <Checkbox checked={!!selected[i.ean]} onCheckedChange={(v) => setSelected(s => ({ ...s, [i.ean]: !!v }))} data-testid={`bf-select-${i.ean}`} />
                      <div className="font-mono font-medium truncate">{i.artikelnummer || i.ean}</div>
                      <div className="truncate text-muted-foreground">{i.produktnamn}</div>
                      <div>{i.antal} st</div>
                      <div>{i.days_since_movement === null ? <Badge variant="outline" className="text-red-500 border-red-500/40">Aldrig</Badge> : `${i.days_since_movement} d`}</div>
                      <div className="font-medium text-emerald-500">{i.unit_price_sek ? kr(i.stock_value_sek) : <span className="text-muted-foreground font-normal">–</span>}</div>
                      <div className="text-xs font-mono truncate">{i.locations}</div>
                      <div><Badge className="bg-primary text-primary-foreground">-{i.suggested_discount}%</Badge></div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        ) : tab === "top" ? (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground"><span className="font-bold text-foreground">{top.count}</span> bäst säljande produkter senaste {months} mån – fyll på dessa inför Black Friday</p>
              <Button className="gap-2" onClick={exportTop} disabled={top.items.length === 0} data-testid="bf-export-top"><Download className="h-4 w-4" />Exportera CSV</Button>
            </div>
            <Card>
              <CardContent className="p-0">
                <div className="grid grid-cols-[40px_150px_1fr_110px_100px] gap-3 p-3 border-b bg-muted/50 text-xs font-medium">
                  <div>#</div><div>Artikel</div><div>Produkt</div><div>Sålt antal</div><div>Nu i lager</div>
                </div>
                <div className="divide-y max-h-[60vh] overflow-auto">
                  {top.items.map((i, idx) => (
                    <div key={i.ean} className="grid grid-cols-[40px_150px_1fr_110px_100px] gap-3 p-3 items-center text-sm hover:bg-muted/30" data-testid={`bf-top-row-${i.ean}`}>
                      <div className="font-bold text-muted-foreground">{idx + 1}</div>
                      <div className="font-mono font-medium truncate">{i.artikelnummer || i.ean}</div>
                      <div className="truncate text-muted-foreground">{i.produktnamn}</div>
                      <div className="font-bold text-emerald-500">{i.qty} st</div>
                      <div className={i.in_stock === 0 ? "text-red-500 font-medium" : ""}>{i.in_stock} st</div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="divide-y">
                {campaigns.length === 0 ? (
                  <div className="p-10 text-center text-muted-foreground"><ListChecks className="h-8 w-8 mx-auto mb-2 opacity-40" />Inga sparade kampanjlistor än</div>
                ) : campaigns.map(c => (
                  <div key={c.id} className="flex items-center justify-between p-4 hover:bg-muted/30" data-testid={`bf-campaign-${c.id}`}>
                    <div className="cursor-pointer flex-1" onClick={() => openCampaign(c.id)}>
                      <p className="font-medium">{c.name}</p>
                      <p className="text-xs text-muted-foreground">{c.item_count} produkter · {kr(c.total_stock_value_sek)} · {String(c.created_at).slice(0, 10)}</p>
                    </div>
                    <Button variant="ghost" size="icon" className="text-destructive" onClick={() => deleteCampaign(c.id)} data-testid={`bf-campaign-delete-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}
      </main>

      {/* Save campaign dialog */}
      <Dialog open={showSave} onOpenChange={setShowSave}>
        <DialogContent className="max-w-md" data-testid="bf-save-dialog">
          <DialogHeader>
            <DialogTitle>Spara kampanjlista</DialogTitle>
            <DialogDescription>Sparar {selectedItems.length || dead.items.length} produkter ({kr((selectedItems.length ? selectedItems : dead.items).reduce((s, i) => s + (i.stock_value_sek || 0), 0))} bundet värde).</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            <Label htmlFor="cname">Namn</Label>
            <Input id="cname" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} placeholder="t.ex. Black Friday 2026 – vitvaror" autoFocus
              onKeyDown={(e) => { if (e.key === "Enter" && campaignName.trim()) saveCampaign(); }} data-testid="bf-campaign-name" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSave(false)}>Avbryt</Button>
            <Button onClick={saveCampaign} disabled={!campaignName.trim()} data-testid="bf-campaign-save-btn">Spara</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View campaign dialog */}
      <Dialog open={!!viewCampaign} onOpenChange={(o) => { if (!o) setViewCampaign(null); }}>
        <DialogContent className="max-w-2xl" data-testid="bf-view-dialog">
          <DialogHeader>
            <DialogTitle>{viewCampaign?.name}</DialogTitle>
            <DialogDescription>{viewCampaign?.item_count} produkter · {kr(viewCampaign?.total_stock_value_sek)}</DialogDescription>
          </DialogHeader>
          <div className="max-h-[50vh] overflow-auto divide-y">
            {(viewCampaign?.items || []).map(i => (
              <div key={i.ean} className="flex items-center justify-between py-2 text-sm">
                <div><span className="font-mono">{i.artikelnummer || i.ean}</span> <span className="text-muted-foreground">{i.produktnamn}</span></div>
                <div className="flex items-center gap-3">
                  <span className="text-emerald-500">{kr(i.stock_value_sek)}</span>
                  <Badge className="bg-primary text-primary-foreground">-{i.suggested_discount}%</Badge>
                </div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button onClick={() => {
              const headers = [
                { label: "Artikelnummer", get: r => r.artikelnummer }, { label: "EAN", get: r => r.ean },
                { label: "Produktnamn", get: r => r.produktnamn }, { label: "Antal", get: r => r.antal },
                { label: "Lagervärde (kr)", get: r => r.stock_value_sek }, { label: "Rabatt (%)", get: r => r.suggested_discount },
              ];
              download(`kampanj_${viewCampaign.name.replace(/\s+/g, "_")}.csv`, toCSV(viewCampaign.items || [], headers));
            }} className="gap-2"><Download className="h-4 w-4" />Exportera CSV</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
